import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  CssBaseline,
  TextField,
  Box,
  Typography,
  Container,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { ACTIONS as MAIN_ACTIONS } from "../../../../redux/main";
import ChipTextInput from "../../../../components/chip-text-input";
import AvatarPicker from "../../../../components/avatar-picker";

import { initState } from "./constants";

const ProfileConfigPage = () => {
  const [formState, setFormState] = useState(initState);
  const { api, user } = useSelector((state) => state.main);
  const dispatch = useDispatch();

  const getHasErrors = (state) => {
    return Object.values(state).reduce((acc, elem) => acc || elem.error, false);
  };

  const onInputChangeHandler = ({ name, data }) => {
    setFormState((p) => {
      return { ...p, [name]: { data: data, error: false } };
    });
  };

  const onSubmitHandler = () => {
    const newState = {};
    const formData = {};

    Object.entries(formState).forEach(([key, val]) => {
      const data = val.data;

      newState[key] = { data: data };
      formData[key] = data;

      if (formState[key]?.notRequired) {
        newState[key].error = false;
      } else if (Array.isArray(data)) {
        newState[key].error = data.length === 0;
      } else {
        newState[key].error = !data;
      }
    });

    const hasErrors = getHasErrors(newState);

    if (!hasErrors) {
      dispatch(MAIN_ACTIONS.initUserInfoDatabase(formData));
    }

    setFormState(newState);
  };

  const onImagePick = (image) => {
    const data = image?.filesContent[0]?.content;
    let extension = image?.plainFiles[0]?.type;
    extension = extension.replace("image/", "");

    onInputChangeHandler({ data: { data, extension }, name: "profilePicture" });
  };

  const onImagePickError = (error) => {
    dispatch(MAIN_ACTIONS.failUploadingImage());
  };

  const nameErr = formState.name.error;
  const surnameErr = formState.surname.error;
  const investigationGroupErr = formState.investigationGroup.error;
  const universityErr = formState.university.error;
  const investigationLinesErr = formState.investigationLines.error;

  const hasErrors = getHasErrors(formState);

  return (
    <Container
      component="main"
      maxWidth="sm"
      sx={{
        marginTop: 8,
        marginBottom: 8,
      }}
    >
      <CssBaseline />

      <Container maxWidth="md">
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexFlow: "column",
          }}
        >
          <Typography variant="h5" sx={{ mb: 2 }}>
            Completa tu perfil
          </Typography>

          <AvatarPicker
            size={120}
            onPick={onImagePick}
            onPickError={onImagePickError}
            image={formState?.profilePicture?.data?.data || user.profilePicture}
          />
        </Box>
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle1">Información personal</Typography>
          <Box>
            <TextField
              margin="normal"
              required
              id="name"
              label="Nombre"
              name="name"
              fullWidth
              onChange={(e) =>
                onInputChangeHandler({ data: e.target.value, name: "name" })
              }
              error={nameErr}
              helperText={nameErr ? "Campo vacío" : ""}
              disabled={api.loading}
            />
            <TextField
              margin="dense"
              required
              id="surname"
              label="Apellidos"
              name="surname"
              fullWidth
              onChange={(e) =>
                onInputChangeHandler({ data: e.target.value, name: "surname" })
              }
              error={surnameErr}
              helperText={surnameErr ? "Campo vacío" : ""}
              disabled={api.loading}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle1">Información académica</Typography>

          <TextField
            margin="dense"
            required
            id="university"
            label="Universidad"
            name="university"
            fullWidth
            onChange={(e) =>
              onInputChangeHandler({ data: e.target.value, name: "university" })
            }
            error={universityErr}
            helperText={universityErr ? "Campo vacío" : ""}
            disabled={api.loading}
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <ChipTextInput
              name="investigationGroup"
              inputProps={{
                margin: "normal",
                required: true,
                id: "investigationGroup",
                label: "Grupos de investigación",
                name: "investigationGroup",

                error: investigationGroupErr,
                helperText: investigationGroupErr ? "Campo vacío" : "",
                disabled: api.loading,
              }}
              onChange={(e) =>
                onInputChangeHandler({ data: e.data, name: e.name })
              }
            />

            <ChipTextInput
              name="investigationLines"
              inputProps={{
                margin: "normal",
                id: "investigationLines",
                label: "Líneas de investigación",
                name: "investigationLines",
                error: investigationLinesErr,
                helperText: investigationLinesErr ? "Campo vacío" : "",
                disabled: api.loading,
              }}
              onChange={(e) =>
                onInputChangeHandler({ data: e.data, name: e.name })
              }
            />
          </Box>
        </Box>

        <LoadingButton
          type="text"
          fullWidth
          variant="contained"
          size="large"
          onClick={onSubmitHandler}
          loading={api.loading}
          disabled={!!api.loading || hasErrors}
        >
          Enviar
        </LoadingButton>
      </Container>
    </Container>
  );
};

export default ProfileConfigPage;
